import { List } from '../domain/list/ListDomains';
import GatewayContainer from '../config/GatewayContainer';
import GatewayNames from '../constant/GatewayNames';
import ListClient from '../client/list/ListClient';
import MessageUtil from '../util/MessageUtil';
import LanguageUtil from '../util/LanguageUtil';
import React from 'react';
import { query } from '@trlogic/client-js';
import ListDataService from './ListDataService';
import { EnrichedComponentCategory } from '../domain/category/CategoryOperationDomains';
import ComponentName from '../constant/ComponentName';
import FolderClient from '../client/folder/FolderClient';
import { dispatch } from '../redux/store';
import { openBulkActionDialogFromDependency } from '../redux/actions/BulkActionActions';
import _ from 'lodash';
import { changeApiVariableDirty } from '../redux/actions/ApiVariableActions';
import { ActionType } from '../constant/Constants';
import { BulkActionDependencyError } from 'redux/reducers/BulkActionReducer';
import DeployErrorUtil from '../util/DeployErrorUtil';

class ListService {
  private listClient: ListClient = GatewayContainer.getInstance().get(GatewayNames.LIST_CLIENT);
  private folderClient: FolderClient = GatewayContainer.getInstance().get(GatewayNames.FOLDER_CLIENT);
  private listDataService: ListDataService;

  constructor(listDataService: ListDataService) {
    this.listDataService = listDataService;
  }

  public saveList = async (_list: List, data: any, isUpdate: boolean, folder: EnrichedComponentCategory) => {
    const list = _.cloneDeep(_list);
    try {
      if (list.hasOwnProperty('productType')) {
        delete (list as any).productType;
      }
      
      const keyField: string | undefined = list.fields && list.fields.filter((column: any) => column.key)[0]?.name;
      if (list.name == undefined || list.name.trim().length == 0) {
        MessageUtil.warning({
          summary: LanguageUtil.get({ id: 'messages.warning.listName', defaultMessage: 'List name is required' }),
        });
        return;
      }
      if (list.fields.length == 0) {
        MessageUtil.warning({
          summary: LanguageUtil.get({ id: 'messages.warning.listField', defaultMessage: 'List fields are required' }),
        });
        return;
      }
      if (keyField == undefined) {
        MessageUtil.warning({
          summary: LanguageUtil.get({ id: 'messages.warning.keyField', defaultMessage: 'Key field is required' }),
        });
        return;
      }
      const isExist = await this.listClient.exist(list.name);
      if (isExist && !isUpdate) {
        MessageUtil.alreadyExist('List');
        return false;
      }
      await this.listClient.save(list);
      if (!isUpdate && folder) {
        await this.folderClient.addItemToCategory(ComponentName.LIST, folder.id, list.name);
      }
      MessageUtil.success({
        summary: LanguageUtil.get({ id: 'common.label.list', defaultMessage: 'List' }),
        detail: (
          <div>
            <b>{list.name}</b> {LanguageUtil.get({ id: 'messages.save', defaultMessage: 'saved.' })}
          </div>
        ),
      });
      dispatch(changeApiVariableDirty(false));
      return true;
    } catch (e) {
      MessageUtil.error({
        summary: LanguageUtil.get({ id: 'common.label.list', defaultMessage: 'List' }),
        detail: (
          <div>
            <b>{list.name}</b> {LanguageUtil.get({ id: 'messages.cannotSaved', defaultMessage: 'not saved' })}
          </div>
        ),
      });
      return false;
    }
  };

  public saveQueryList = async (list: List, isUpdate: boolean) => {
    try {
      if (list.hasOwnProperty('productType')) {
        delete (list as any).productType;
      }
      
      const keyField: string | undefined = list.fields && list.fields.filter((column: any) => column.key)[0]?.name;
      if (list.name == undefined || list.name.trim().length == 0) {
        MessageUtil.warning({
          summary: LanguageUtil.get({ id: 'messages.warning.listName', defaultMessage: 'List name is required' }),
        });
        return;
      }
      if (list.fields.length == 0) {
        MessageUtil.warning({
          summary: LanguageUtil.get({ id: 'messages.warning.listFields', defaultMessage: 'List fields are required' }),
        });
        return;
      }
      if (keyField == undefined) {
        MessageUtil.warning({
          summary: LanguageUtil.get({ id: 'messages.warning.keyField', defaultMessage: 'Key field is required' }),
        });
        return;
      }
      if (list.query == undefined || list.query.trim().length == 0) {
        MessageUtil.warning({
          summary: LanguageUtil.get({ id: 'messages.warning.query', defaultMessage: 'Query is required' }),
        });
        return;
      }
      if (list.cronExpression == undefined || list.cronExpression.trim().length == 0) {
        MessageUtil.warning({
          summary: LanguageUtil.get({ id: 'messages.warning.cron', defaultMessage: 'Cron Expression is required' }),
        });
        return;
      }
      if (list.connectionName == undefined || list.connectionName.trim().length == 0) {
        MessageUtil.warning({
          summary: LanguageUtil.get({ id: 'messages.warning.conName', defaultMessage: 'Connection Name is required' }),
        });
        return;
      }
      const isExist = await this.listClient.exist(list.name);
      if (isExist && !isUpdate) {
        MessageUtil.alreadyExist(LanguageUtil.get({ id: 'common.label.list', defaultMessage: 'List' }));
        return false;
      }
      await this.listClient.save(list);
      MessageUtil.success({
        summary: LanguageUtil.get({ id: 'common.label.list', defaultMessage: 'List' }),
        detail: (
          <div>
            <b>{list.name}</b> {LanguageUtil.get({ id: 'messages.save', defaultMessage: 'saved.' })}
          </div>
        ),
      });
      return true;
    } catch (e) {
      MessageUtil.error({
        summary: LanguageUtil.get({ id: 'common.label.list', defaultMessage: 'List' }),
        detail: (
          <div>
            <b>{list.name}</b> {LanguageUtil.get({ id: 'messages.cannotSaved', defaultMessage: 'not saved' })}
          </div>
        ),
      });
      return false;
    }
  };

  public removelist = async (list: List) => {
    try {
      const response = await this.listClient.remove(list.name);
      if (response.code) {
        return false;
      }
      MessageUtil.success({
        summary: LanguageUtil.get({ id: 'common.label.list', defaultMessage: 'List' }),
        detail: (
          <div>
            <b>{list.name}</b> {LanguageUtil.get({ id: 'messages.removed', defaultMessage: 'removed' })}
          </div>
        ),
      });
      return true;
    } catch (e: any) {
      if (DeployErrorUtil.show(e, LanguageUtil.get({ id: 'common.label.list', defaultMessage: 'List' }))) {
        return false;
      }
      const dependencies: any = e.response.data;
      if (dependencies?.message == 'Dependency Exception') {
        dispatch(openBulkActionDialogFromDependency(dependencies, ComponentName.LIST));
      }
      MessageUtil.error({
        summary: LanguageUtil.get({ id: 'common.label.list', defaultMessage: 'List' }),
        detail: (
          <div>
            <b>{list.name}</b> {LanguageUtil.get({ id: 'messages.cannotRemoved', defaultMessage: 'can not remove' })}
          </div>
        ),
      });
      return false;
    }
  };

  public deployList = async (list: List) => {
    if (!(list.name && list.name.trim().length > 0 && list.fields.length > 0 && list.id != undefined)) {
      return;
    }
    try {  
      await this.listClient.deploy(list.name)

      MessageUtil.success({
        summary: LanguageUtil.get({ id: 'common.label.list', defaultMessage: 'List' }),
        detail: (
          <div>
            <b>{list.name}</b> {LanguageUtil.get({ id: 'messages.deploy', defaultMessage: 'deployed' })}
          </div>
        ),
      });
    } catch (e) {
      if (DeployErrorUtil.show(e, LanguageUtil.get({ id: 'common.label.list', defaultMessage: 'List' }))) {
        return;
      }
      MessageUtil.error({
        summary: LanguageUtil.get({ id: 'common.label.list', defaultMessage: 'List' }),
        detail: (
          <div>
            <b>{list.name}</b> {LanguageUtil.get({ id: 'messages.cannotdeployed', defaultMessage: 'can not deployed' })}
          </div>
        ),
      });
    }
  };

  public async bulkAction(ids: string[], type: ActionType): Promise<BulkActionDependencyError[]> {
    const logs: any = await this.listClient.bulkAction(ids, type);
    return logs;
  }

  public undeployList = async (list: List) => {
    if (!(list.name && list.name.trim().length > 0 && list.fields.length > 0 && list.id != undefined)) {
      return;
    }
    try {    
      await this.listClient.undeploy(list.name);
      MessageUtil.success({
        summary: LanguageUtil.get({ id: 'common.label.list', defaultMessage: 'List' }),
        detail: (
          <div>
            <b>{list.name}</b> {LanguageUtil.get({ id: 'messages.undeployed', defaultMessage: 'undeployed' })}
          </div>
        ),
      });
    } catch (e) {
      if (DeployErrorUtil.show(e, LanguageUtil.get({ id: 'common.label.list', defaultMessage: 'List' }))) {
        return;
      }
      MessageUtil.error({
        summary: LanguageUtil.get({ id: 'common.label.list', defaultMessage: 'List' }),
        detail: (
          <div>
            <b>{list.name}</b> {LanguageUtil.get({ id: 'messages.cannotUndeployed', defaultMessage: 'can not undeployed' })}
          </div>
        ),
      });
    }
  };

  public getCount = (_query: query.Query) => {
    return this.listClient.getCount(_query);
  };

  public findById = (id: string): Promise<List | undefined> => {
    return this.listClient.findById(id);
  };
}

export default ListService;
